import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, FileText, CheckCircle2, XCircle, ShieldOff, ChevronDown, ChevronRight } from "lucide-react";
import DocumentUpload from "@/components/app/DocumentUpload";
import ImpersonateButton from "@/components/app/ImpersonateButton";

const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AdminDealers() {
  const [tab, setTab] = useState("pending"); // pending | active | all-notes
  const [dealers, setDealers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const ds = await api.get("/admin/dealers");
      setDealers(ds.data || []);
    } catch { /* ignore */ }
    try {
      const ns = await api.get("/admin/dealer-notes?status=pending");
      setNotes(ns.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const approveDealer = async (uid) => {
    await api.post(`/admin/dealers/${uid}/approve`);
    toast.success("Dealer approved");
    load();
  };
  const rejectDealer = async (uid) => {
    const reason = window.prompt("Reason for rejection (sent to dealer):");
    if (reason === null) return;
    await api.post(`/admin/dealers/${uid}/reject`, { reason });
    toast.success("Rejected");
    load();
  };
  const suspendDealer = async (uid) => {
    if (!window.confirm("Suspend this dealer? Their active listings will be unlisted.")) return;
    await api.post(`/admin/dealers/${uid}/suspend`);
    toast.success("Suspended");
    load();
  };
  const approveNote = async (id) => {
    await api.post(`/admin/dealer-notes/${id}/approve`, {});
    toast.success("Note approved — now live");
    load();
  };
  const rejectNote = async () => {
    if (!reason.trim()) { toast.error("Please add a reason"); return; }
    await api.post(`/admin/dealer-notes/${reasonFor}/reject`, { reason });
    toast.success("Note rejected");
    setReasonFor(null); setReason("");
    load();
  };

  const pendingDealers = dealers.filter((d) => d.status === "pending_verification");
  const activeDealers = dealers.filter((d) => d.status === "approved");
  const suspendedDealers = dealers.filter((d) => d.status === "suspended" || d.status === "rejected");

  return (
    <div className="space-y-6" data-testid="admin-dealers">
      <div className="flex gap-2 border-b border-border">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} count={pendingDealers.length + notes.length} tid="admin-dealers-tab-pending">Pending</TabBtn>
        <TabBtn active={tab === "active"} onClick={() => setTab("active")} count={activeDealers.length} tid="admin-dealers-tab-active">Active</TabBtn>
        <TabBtn active={tab === "suspended"} onClick={() => setTab("suspended")} count={suspendedDealers.length} tid="admin-dealers-tab-suspended">Suspended</TabBtn>
      </div>

      {tab === "pending" && (
        <>
          <SectionTitle icon={Building2} label="Pending Dealer Applications" count={pendingDealers.length} />
          {pendingDealers.length === 0 ? (
            <Empty>No pending dealer applications.</Empty>
          ) : pendingDealers.map((d) => (
            <DealerRow key={d.dealer_id} d={d}
              actions={
                <>
                  <Button size="sm" className="rounded-none" onClick={() => approveDealer(d.user_id)}
                    data-testid={`admin-approve-dealer-${d.dealer_id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-none" onClick={() => rejectDealer(d.user_id)}
                    data-testid={`admin-reject-dealer-${d.dealer_id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </>
              } />
          ))}

          <SectionTitle icon={FileText} label="Pending Note Submissions" count={notes.length} />
          {notes.length === 0 ? (
            <Empty>No notes awaiting review.</Empty>
          ) : notes.map((n) => (
            <div key={n.note_id} className="border border-border p-4" data-testid={`admin-pending-note-${n.note_id}`}>
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div>
                  <div className="font-mono-data text-sm">
                    {n.vehicle_year} {n.vehicle_make} {n.vehicle_model} · {n.state} · Grade {n.grade}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    From {n.dealer_name} · Yield {n.estimated_yield}% · Ask {fmtMoney(n.asking_price)} · Bal {fmtMoney(n.remaining_balance)}
                  </div>
                  {n.description && <p className="text-xs mt-2 text-muted-foreground italic">&quot;{n.description}&quot;</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-none" onClick={() => approveNote(n.note_id)}
                    data-testid={`admin-approve-note-${n.note_id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-none"
                    onClick={() => { setReasonFor(n.note_id); setReason(""); }}
                    data-testid={`admin-reject-note-${n.note_id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
              {reasonFor === n.note_id && (
                <div className="mt-3 flex gap-2 items-center" data-testid={`admin-reject-form-${n.note_id}`}>
                  <Input className="rounded-none" placeholder="Rejection reason"
                    value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-reject-reason" />
                  <Button size="sm" variant="destructive" onClick={rejectNote} className="rounded-none" data-testid="admin-reject-confirm">Confirm</Button>
                  <Button size="sm" variant="outline" onClick={() => { setReasonFor(null); setReason(""); }} className="rounded-none">Cancel</Button>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {tab === "active" && (
        <>
          {activeDealers.length === 0 ? (
            <Empty>No active dealers.</Empty>
          ) : activeDealers.map((d) => (
            <DealerRow key={d.dealer_id} d={d}
              actions={
                <div className="flex gap-2 flex-wrap">
                  <ImpersonateButton userId={d.user_id} userEmail={d.email} userRole="dealer"
                    label="View as dealer" />
                  <Button size="sm" variant="destructive" className="rounded-none" onClick={() => suspendDealer(d.user_id)}
                    data-testid={`admin-suspend-dealer-${d.dealer_id}`}>
                    <ShieldOff className="w-4 h-4 mr-1" /> Suspend
                  </Button>
                </div>
              } />
          ))}
        </>
      )}

      {tab === "suspended" && (
        <>
          {suspendedDealers.length === 0 ? <Empty>No suspended dealers.</Empty> :
            suspendedDealers.map((d) => (
              <DealerRow key={d.dealer_id} d={d}
                actions={
                  <Button size="sm" className="rounded-none" onClick={() => approveDealer(d.user_id)}
                    data-testid={`admin-restore-dealer-${d.dealer_id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Reinstate
                  </Button>
                } />
            ))}
        </>
      )}
    </div>
  );
}

function TabBtn({ active, children, onClick, count, tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`px-4 py-2 text-xs font-overline border-b-2 ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
      {children} {count > 0 && `(${count})`}
    </button>
  );
}
function SectionTitle({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-primary" />
      <span className="font-overline text-accent">{label}</span>
      {count != null && <span className="text-muted-foreground text-xs">({count})</span>}
    </div>
  );
}
function Empty({ children }) {
  return <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{children}</div>;
}
function DealerRow({ d, actions }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border" data-testid={`dealer-row-${d.dealer_id}`}>
      <div className="p-4 flex flex-wrap items-start gap-3 justify-between">
        <button className="flex items-start gap-2 text-left flex-1 hover:text-primary"
          onClick={() => setOpen(!open)} data-testid={`dealer-expand-${d.dealer_id}`}>
          {open ? <ChevronDown className="w-4 h-4 mt-1" /> : <ChevronRight className="w-4 h-4 mt-1" />}
          <div className="flex-1">
            <div className="font-mono-data">{d.business_name}</div>
            <div className="text-xs text-muted-foreground">
              {d.contact_name || "—"} · {d.email} · {d.state || "—"} · EIN {d.ein || "—"} · License {d.dealer_license || "—"}
            </div>
            {d.notes && <p className="text-xs mt-1 text-muted-foreground italic">&quot;{d.notes}&quot;</p>}
          </div>
        </button>
        <div className="flex gap-2">{actions}</div>
      </div>
      {open && (
        <div className="border-t border-border bg-muted/20 p-4 space-y-4" data-testid={`dealer-details-${d.dealer_id}`}>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <KV k="DBA" v={d.dba_name} />
            <KV k="Phone" v={d.phone} />
            <KV k="EIN" v={d.ein} />
            <KV k="Dealer License #" v={d.dealer_license} />
            <KV k="Finance License #" v={d.finance_license} />
            <KV k="Business Address" v={d.business_address} />
            <KV k="State" v={d.state} />
            <KV k="Website" v={d.website} />
          </div>
          <DocumentUpload
            entityType="dealer_profile"
            entityId={d.dealer_id}
            isAdmin
            readOnly
            showAll
            title="Dealer Documents"
          />
        </div>
      )}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground text-[10px]">{k}</div>
      <div className="font-mono-data text-sm">{v || "—"}</div>
    </div>
  );
}
